/**
 * Auth API wrappers.
 *
 * Every auth-related HTTP call the frontend makes goes through this
 * module. The functions return the axios `data` payload directly so
 * pages don't touch the transport.
 *
 * Unauthenticated endpoints (login / register / forgot / reset / verify)
 * use a plain axios instance so the interceptor loop doesn't kick in on
 * responses that were never expected to carry a Bearer token. `getMe`
 * and other authenticated calls use the shared `authClient`.
 */

import axios from 'axios';
import authClient from '../auth/authClient.js';
import { setTokens } from '../auth/tokenStorage.js';

const BASE_URL = (import.meta.env?.VITE_API_URL || '').replace(/\/+$/, '');
const publicClient = axios.create({ baseURL: BASE_URL });

// ── Unified passwordless OTP (public customer auth) ────────────────
//
// One entry point → one verify outcome ('authenticated' with tokens,
// or 'onboarding_required' with a short-lived onboardingToken). The
// frontend uses the outcome to decide whether to route the user home
// or into the onboarding step.

export async function requestOtpRequest({ email }) {
  const { data } = await publicClient.post('/auth/otp/request', { email });
  return data;
}

export async function verifyOtpRequest({ challengeId, otp }) {
  const { data } = await publicClient.post('/auth/otp/verify', { challengeId, otp });
  // Only stash tokens when the outcome actually returned a session —
  // the onboarding_required outcome intentionally does NOT include
  // access/refresh tokens.
  if (data?.outcome === 'authenticated' && data?.accessToken && data?.refreshToken) {
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  }
  return data;
}

export async function resendOtpRequest({ challengeId }) {
  const { data } = await publicClient.post('/auth/otp/resend', { challengeId });
  return data;
}

export async function completeOnboardingRequest({ onboardingToken, firstName, lastName, phone }) {
  const { data } = await publicClient.post('/auth/onboarding/complete', {
    onboardingToken, firstName, lastName, phone,
  });
  if (data?.accessToken && data?.refreshToken) {
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  }
  return data;
}

// ── Unauthenticated (legacy password endpoints — no longer surfaced ──
//    in the public customer UI, kept for programmatic callers.) ──────

export async function loginRequest({ email, password }) {
  const { data } = await publicClient.post('/auth/login', { email, password });
  if (data?.accessToken && data?.refreshToken) {
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  }
  return data;
}

export async function registerRequest({ email, password, firstName, lastName, phone }) {
  const { data } = await publicClient.post('/auth/register', {
    email, password, firstName, lastName, phone,
  });
  if (data?.accessToken && data?.refreshToken) {
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  }
  return data;
}

export async function forgotPasswordRequest({ email }) {
  const { data } = await publicClient.post('/auth/forgot-password', { email });
  return data;
}

export async function resetPasswordRequest({ email, token, password }) {
  const { data } = await publicClient.post('/auth/reset-password', { email, token, password });
  return data;
}

export async function verifyEmailRequest({ token }) {
  const { data } = await publicClient.post('/auth/verify-email', { token });
  return data;
}

export async function resendVerificationRequest({ email }) {
  const { data } = await publicClient.post('/auth/resend-verification', { email });
  return data;
}

// ── Authenticated: email change (OTP-verified) ─────────────────────
//
// Dedicated flow — deliberately NOT reusing PATCH /auth/me because
// email changes need a separate proof-of-ownership step on the new
// address. The backend contract is:
//
//   POST /auth/email/change/request { newEmail }
//     → 200 { challengeId, resendCooldownSeconds, otpExpiresSeconds }
//     → 409 { error: 'EMAIL_TAKEN' } (never leaks which account)
//     → 429 { error: 'RATE_LIMITED', retryAfterSeconds }
//     → 404 (endpoint not yet deployed — UI surfaces a clean
//        "feature not yet available" state without a stack trace)
//
//   POST /auth/email/change/verify { challengeId, otp }
//     → 200 { user }   ← previous email retained until this returns
//     → 400 { error: 'OTP_INVALID' | 'OTP_EXPIRED' }
//     → 429 { error: 'TOO_MANY_ATTEMPTS', retryAfterSeconds }
//
// The old email must receive a security notification server-side
// AFTER verify succeeds — that's a backend responsibility.

export async function requestEmailChangeRequest({ newEmail }) {
  const { data } = await authClient.post('/auth/email/change/request', { newEmail });
  return data;
}

export async function verifyEmailChangeRequest({ challengeId, otp }) {
  const { data } = await authClient.post('/auth/email/change/verify', { challengeId, otp });
  return data;
}

// ── Authenticated ───────────────────────────────────────────────────

export async function getMeRequest() {
  const { data } = await authClient.get('/auth/me');
  return data;
}

export async function logoutRequest({ refreshToken }) {
  // Best-effort — a network failure here should not block clearing
  // local state, so callers ignore this promise's rejection.
  try {
    await publicClient.post('/auth/logout', { refreshToken });
  } catch { /* swallow */ }
}
